import type { FolderSortDir, FolderSortKey } from './folder-sort';

/**
 * Préférence UTILISATEUR : tri des CARTES DE DOSSIER (onglet Dossiers) — critère
 * Nom/Date + sens — stockée côté client en `localStorage`, même patron que la densité
 * (`density-pref.ts`) et que les préférences de tri de la galerie
 * (`default-sort-pref.ts`).
 *
 * Le sélecteur repartait à zéro à chaque visite (`useState('name')`), alors que toutes
 * les autres préférences d'affichage survivent : redescendre dans l'arborescence
 * réimposait « Nom croissant » à quelqu'un qui venait de choisir « Date décroissante ».
 *
 * Elle reste HORS de l'URL à dessein : ce tri ne porte que sur des CARTES (facette de
 * 1er niveau, tri purement client) — ce n'est pas l'ensemble de photos décrit par le
 * lien, donc pas un « emplacement » adressable.
 *
 * Module PUR & testé, tolérant si `localStorage` est indisponible (mode privé strict,
 * quotas) : repli en mémoire, jamais d'exception.
 */

/** Critère + sens de tri des cartes de dossier. */
export interface FolderSortPref {
  key: FolderSortKey;
  dir: FolderSortDir;
}

/** Clé de persistance (namespace `xphoto.*`, comme la densité). */
export const FOLDER_SORT_STORAGE_KEY = 'xphoto.gallery.folderSort';

/** Défaut de fabrique INCHANGÉ : par nom, croissant (valeurs historiques du `useState`). */
export const FOLDER_SORT_FALLBACK: FolderSortPref = { key: 'name', dir: 'asc' };

/** Allow-lists (les deux enums sont locaux à `folder-sort.ts`, pas des schémas de contrat). */
const KEYS: readonly string[] = ['name', 'date'];
const DIRS: readonly string[] = ['asc', 'desc'];

/** Repli mémoire quand `localStorage` est inaccessible (jamais d'exception). */
let memoryPref: FolderSortPref | null = null;

/**
 * Décode la valeur persistée (JSON `{"key":…,"dir":…}`) en couple VALIDE, ou `null`.
 * Tout ce qui n'est pas entièrement valide est traité comme ABSENT → défaut de fabrique.
 */
function parsePref(raw: string | null): FolderSortPref | null {
  if (raw === null) return null;
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof decoded !== 'object' || decoded === null) return null;
  const record = decoded as Record<string, unknown>;
  const key = record.key;
  const dir = record.dir;
  if (typeof key !== 'string' || !KEYS.includes(key)) return null;
  if (typeof dir !== 'string' || !DIRS.includes(dir)) return null;
  return { key: key as FolderSortKey, dir: dir as FolderSortDir };
}

/**
 * Lit la préférence persistée, ou `null` si absente/invalide. Le repli MÉMOIRE n'est
 * consulté QUE si `localStorage` est INDISPONIBLE — pas quand il renvoie simplement
 * `null` : vider le stockage doit bien remettre au défaut (même discipline qu'ailleurs).
 */
export function readStoredFolderSort(): FolderSortPref | null {
  try {
    if (typeof localStorage === 'undefined') return memoryPref;
    return parsePref(localStorage.getItem(FOLDER_SORT_STORAGE_KEY));
  } catch {
    return memoryPref;
  }
}

/** Tri de cartes EFFECTIF : persisté sinon le défaut de fabrique (nom croissant). */
export function getFolderSort(): FolderSortPref {
  return readStoredFolderSort() ?? FOLDER_SORT_FALLBACK;
}

/** Persiste le tri de cartes (repli mémoire + `localStorage`, silencieux si indisponible). */
export function storeFolderSort(pref: FolderSortPref): void {
  if (!KEYS.includes(pref.key) || !DIRS.includes(pref.dir)) return;
  const valid: FolderSortPref = { key: pref.key, dir: pref.dir };
  memoryPref = valid;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(FOLDER_SORT_STORAGE_KEY, JSON.stringify(valid));
    }
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}
